import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { StoryAtlasPanel } from './StoryAtlasPanel';
import { AudienceLockDialog } from './AudienceLockDialog';
import { HotspotWorkspace } from './HotspotWorkspace';
import { DialogueWorkspace } from './DialogueWorkspace';
import { InitializeConfirmDialog } from './InitializeConfirmDialog';
import { AgentWorkflowDialog } from './AgentWorkflowDialog';
import { DirectorWorkflowDialog } from '../director/DirectorWorkflowDialog';
import { useWriterStore } from '@/stores/writer';
import { useAppStore } from '@/stores/app';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { ExportScriptDropdown, type EpisodeRecord } from './scriptExport';
import type { IpSeed } from '@starfox/shared-types';
import { isFirstPersonScriptGenre } from '@starfox/shared-types';
import { useVideoStyles } from '@/hooks/useVideoStyles';
import {
  Sparkles,
  MessageSquare,
  AlertTriangle,
  Lock,
  ArrowLeft,
  CircleDot,
  CheckCircle2,
  Clapperboard,
  Layers,
  Pencil,
  Check,
  X,
} from 'lucide-react';

const paths = [
  { id: 'path1', label: '热点驱动', icon: Sparkles, hint: '来自雷达 Topic Card' },
  { id: 'path3', label: '脚本微调', icon: MessageSquare, hint: '已生成分集脚本 · AI 对话润色' },
] as const;

type PathId = (typeof paths)[number]['id'];

type InitializePayload = {
  title?: string;
  genreTags?: string[];
  reset?: boolean;
};

export interface AudienceState {
  primary?: string;
  secondary?: string[];
  ageRange?: [number, number] | null;
  gender?: 'all' | 'female' | 'male';
  regions?: string[];
  tags?: string[];
  locked: boolean;
  lockedAt?: string | null;
}

export interface EpisodeContext {
  episodeId: string;
  projectId?: string;
  topicId?: string;
  topicTitle?: string;
  kickoffPath: PathId;
  ipSeed: IpSeed | null;
  audience: AudienceState;
  ssmlDoc: {
    meta: Record<string, unknown>;
    sceneCount: number;
    updatedAt: string | null;
  };
  atlasSummary: {
    outline: unknown[];
    hooks: unknown[];
    mood: unknown[];
    charactersCount: number;
  };
  status: 'draft' | 'initialized';
  initializedAt?: string | null;
}

export function WriterPage() {
  const params = useParams({ strict: false }) as { episodeId?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: VIDEO_STYLES = [] } = useVideoStyles();
  const episodeId = params.episodeId;
  const streaming = useWriterStore((s) => s.streaming);
  const setDoc = useWriterStore((s) => s.setDoc);
  const setAtlasTab = useAppStore((s) => s.setAtlasTab);
  const [activePath, setActivePath] = useState<PathId>('path1');
  const [lockOpen, setLockOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeVideoStyle, setCompleteVideoStyle] = useState<string>('二次元动漫');
  const [completeCustomStyle, setCompleteCustomStyle] = useState<string>('');
  const [completeAspectRatio, setCompleteAspectRatio] = useState<'9:16' | '16:9'>(
    '9:16',
  );
  const [directorOpen, setDirectorOpen] = useState(false);
  const [autoOpenedFor, setAutoOpenedFor] = useState<string | null>(null);
  const [autoDirectorOpenedFor, setAutoDirectorOpenedFor] = useState<string | null>(null);

  const query = useQuery<EpisodeContext>({
    queryKey: ['episode', episodeId],
    queryFn: () => api.get(`writer/episodes/${episodeId}`).json<EpisodeContext>(),
    enabled: Boolean(episodeId),
    retry: false,
  });

  useEffect(() => {
    const data = query.data;
    if (!data) return;
    const metaNow = (data.ssmlDoc?.meta ?? {}) as Record<string, unknown>;
    const agentDone =
      typeof metaNow.agentCompletedAt === 'string' && metaNow.agentCompletedAt.length > 0;
    const kickoff = data.kickoffPath === 'path3' ? 'path3' : 'path1';
    // draft 阶段 / Agent 未完成时强制停在 path1
    setActivePath(data.status === 'draft' || !agentDone ? 'path1' : kickoff);
    // @ts-expect-error ssmlDoc summary 与完整 SSML 形状不同,但 store 只暂存供后续 stream 覆盖
    setDoc(data.ssmlDoc ?? null);
  }, [query.data, setDoc]);

  useEffect(() => {
    const err = query.error as { response?: Response } | undefined;
    if (err?.response && err.response.status === 404) {
      toast.error('剧集不存在', '请回到热点雷达重新立项');
      navigate({ to: '/radar' });
    }
  }, [query.error, navigate]);

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ['episode', episodeId] });
    qc.invalidateQueries({ queryKey: ['atlas', episodeId] });
  };

  const initializeMut = useMutation({
    mutationFn: async (payload: InitializePayload) =>
      api
        .post(`writer/episodes/${episodeId}/initialize`, { json: payload })
        .json<{ status: string; initializedAt: string }>(),
    onSuccess: (_, payload) => {
      if (payload.reset) {
        toast.success('已重新项目初始化', '将覆盖重新生成分集 S 场景与 Story Atlas');
      } else {
        toast.success('项目已初始化', '编剧 Agent 即将开始生成分集脚本与 Atlas');
      }
      setConfirmOpen(false);
      setActivePath('path1');
      setAgentOpen(true);
      refetchAll();
    },
    onError: (err: Error) => toast.error('初始化失败', err.message),
  });

  const ctx = query.data;
  const ipSeed = ctx?.ipSeed ?? null;
  const audience = ctx?.audience ?? { locked: false };
  const status: 'draft' | 'initialized' = ctx?.status ?? 'draft';
  const isDraft = status === 'draft';
  const meta = (ctx?.ssmlDoc?.meta ?? {}) as Record<string, unknown>;
  const inferredIpEpisodeCount = inferEpisodeCountFromStoryline(ipSeed?.storyline);
  const hasSelectedEpisodeCount =
    (typeof meta.episodeCount === 'number' && meta.episodeCount >= 1) ||
    (typeof ipSeed?.episodeCount === 'number' && ipSeed.episodeCount >= 1) ||
    inferredIpEpisodeCount >= 1;
  const episodeCount =
    typeof meta.episodeCount === 'number' && meta.episodeCount >= 1
      ? (meta.episodeCount as number)
      : typeof ipSeed?.episodeCount === 'number' && ipSeed.episodeCount >= 1
        ? ipSeed.episodeCount
        : inferredIpEpisodeCount >= 1
          ? inferredIpEpisodeCount
        : 60;
  const episodeDuration: number =
    typeof meta.episodeDuration === 'number' && [3, 5, 10].includes(meta.episodeDuration)
      ? (meta.episodeDuration as number)
      : typeof ipSeed?.episodeDuration === 'number' && [3, 5, 10].includes(ipSeed.episodeDuration)
        ? ipSeed.episodeDuration
      : 3;
  const scriptMode =
    typeof meta.mode === 'string' && meta.mode.trim().length > 0
      ? (meta.mode as string)
      : null;
  const storylineWorld = typeof meta.storylineWorld === 'string' ? (meta.storylineWorld as string) : '';
  const storylineCharacters = typeof meta.storylineCharacters === 'string' ? (meta.storylineCharacters as string) : '';
  const storylineEpisodes = useMemo(() => {
    const raw = Array.isArray(meta.storylinePlots) ? (meta.storylinePlots as unknown[]) : [];
    return raw
      .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
      .map((e) => ({
        index: typeof e.index === 'number' ? e.index : 0,
        title: typeof e.title === 'string' ? e.title : undefined,
        narrative: typeof e.narrative === 'string' ? e.narrative : undefined,
        updatedAt: typeof e.updatedAt === 'string' ? e.updatedAt : undefined,
      }))
      .filter((e) => e.index > 0);
  }, [meta]);
  const fullStorylineRaw = meta.fullStoryline as
    | { text?: string; episodeCount?: number; updatedAt?: string }
    | undefined;
  const fullStoryline =
    fullStorylineRaw && typeof fullStorylineRaw.text === 'string' && fullStorylineRaw.text.length > 0
      ? {
          text: fullStorylineRaw.text,
          episodeCount:
            typeof fullStorylineRaw.episodeCount === 'number'
              ? fullStorylineRaw.episodeCount
              : episodeCount,
        }
      : null;
  // 题材标签预选:已初始化 → meta.genreTags;首次创建 → meta.topicGenres(雷达 IP 化打的标签)
  const initialGenreTags = useMemo(() => {
    const fromInit = Array.isArray(meta.genreTags) ? (meta.genreTags as unknown[]) : [];
    const fromTopic = Array.isArray(meta.topicGenres) ? (meta.topicGenres as unknown[]) : [];
    const src = fromInit.length > 0 ? fromInit : fromTopic;
    return src.filter((t): t is string => typeof t === 'string' && t.trim().length > 0);
  }, [meta]);
  // 冷感生存日志等第一人称题材:脚本是机械硬切分,无需 AI 润色(但保留场景查看)。
  const hideRefine = isFirstPersonScriptGenre(initialGenreTags);
  const agentCompletedAt =
    typeof meta.agentCompletedAt === 'string' && meta.agentCompletedAt.length > 0
      ? (meta.agentCompletedAt as string)
      : null;
  const agentInProgress = status === 'initialized' && !agentCompletedAt;
  const writerCompletedAt =
    typeof meta.writerCompletedAt === 'string' && meta.writerCompletedAt.length > 0
      ? (meta.writerCompletedAt as string)
      : null;
  const directorInitializedAt =
    typeof meta.directorInitializedAt === 'string' && meta.directorInitializedAt.length > 0
      ? (meta.directorInitializedAt as string)
      : null;

  const completeStyleEffective =
    completeCustomStyle.trim() || completeVideoStyle.trim();
  const completeReady =
    !!completeStyleEffective && !!completeAspectRatio;

  const completeMut = useMutation({
    mutationFn: async () =>
      api
        .post(`writer/episodes/${episodeId}/complete`, {
          json: {
            videoStyle: completeStyleEffective,
            aspectRatio: completeAspectRatio,
          },
        })
        .json<{ writerCompletedAt: string }>(),
    onSuccess: () => {
      toast.success('已完成编剧', '即将开始制作台初始化');
      setCompleteOpen(false);
      qc.invalidateQueries({ queryKey: ['writer', 'projects'] });
      qc.invalidateQueries({ queryKey: ['episode', episodeId] });
      setDirectorOpen(true);
    },
    onError: (err: Error) => toast.error('完成编剧失败', err.message),
  });

  // 把 SsmlDoc.meta.episodes 转成 AgentWorkflowDialog 可消费的 initialEpisodes,
  // 让弹窗一打开就显示真实进度,不要再从 0/N 起跳
  const initialAgentEpisodes = useMemo(() => {
    const list = Array.isArray(meta.episodes) ? (meta.episodes as unknown[]) : [];
    return list
      .map((raw) => {
        if (!raw || typeof raw !== 'object') return null;
        const r = raw as Record<string, unknown>;
        const idx = typeof r.index === 'number' ? r.index : null;
        if (idx === null || idx < 1) return null;
        const scenes = Array.isArray(r.scenes) ? (r.scenes as unknown[]) : [];
        return {
          index: idx,
          title: typeof r.title === 'string' ? r.title : undefined,
          summary: typeof r.summary === 'string' ? r.summary : undefined,
          sceneCount: scenes.length,
          charCount: typeof r.charCount === 'number' ? r.charCount : 0,
          hasScenes: scenes.length > 0,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [meta]);

  // 完整 EpisodeRecord 列表,供脚本导出下拉使用
  const exportEpisodes = useMemo<EpisodeRecord[]>(() => {
    const list = Array.isArray(meta.episodes) ? (meta.episodes as EpisodeRecord[]) : [];
    return [...list].sort((a, b) => (a.index || 0) - (b.index || 0));
  }, [meta]);

  // 状态为已初始化但 Agent 未完成时,自动打开进度弹窗(每个 episode 仅触发一次)
  useEffect(() => {
    if (!episodeId) return;
    if (!agentInProgress) return;
    if (autoOpenedFor === episodeId) return;
    setAgentOpen(true);
    setAutoOpenedFor(episodeId);
  }, [episodeId, agentInProgress, autoOpenedFor]);

  // 完成编剧但制作台尚未初始化(或正在初始化中)→ 自动打开 Director Dialog
  useEffect(() => {
    if (!episodeId) return;
    if (!writerCompletedAt) return;
    if (directorInitializedAt) return;
    if (autoDirectorOpenedFor === episodeId) return;
    setDirectorOpen(true);
    setAutoDirectorOpenedFor(episodeId);
  }, [episodeId, writerCompletedAt, directorInitializedAt, autoDirectorOpenedFor]);
  const summaryChips = useMemo(() => {
    if (!ctx) return [];
    const chips: { label: string; value: string }[] = [];
    if (ctx.topicTitle) chips.push({ label: '来源', value: `雷达 · ${ctx.topicTitle}` });
    if (ctx.episodeId) chips.push({ label: '剧集', value: ctx.episodeId });
    return chips;
  }, [ctx]);

  if (!episodeId) {
    return (
      <div className="flex h-[calc(100vh-3.5rem)] flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-neutral-600">
          还没有打开剧集 · 请到热点雷达选择一个 Topic 并立项,或到项目大厅挑一个已立项的项目
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button onClick={() => navigate({ to: '/radar' })}>
            <ArrowLeft className="h-4 w-4" /> 去热点雷达
          </Button>
          <Button variant="secondary" onClick={() => navigate({ to: '/projects' })}>
            <Layers className="h-4 w-4" /> 前往项目大厅
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      <section className="flex-1 overflow-y-auto p-6 lg:p-8">
        <header className="mb-6 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-semibold tracking-tight">编剧工作台</h1>
            <p className="mt-1 text-sm text-neutral-600">
              选择创作路径 · 与 AI 共同打磨 SSML 剧本 · 右侧 Atlas 实时显示多维分析
            </p>
            {query.isLoading ? (
              <div className="mt-3 h-6 w-1/2 animate-pulse rounded bg-surface-variant" />
            ) : (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-neutral-600">
                <StatusChip status={status} initializedAt={ctx?.initializedAt ?? null} />
                {ipSeed?.ipName !== undefined && episodeId && (
                  <EditableIpName
                    episodeId={episodeId}
                    value={ipSeed?.ipName ?? ''}
                  />
                )}
                {summaryChips.map((c) => (
                  <span
                    key={c.label}
                    className="inline-flex items-center gap-1 rounded-full border border-outline-variant/40 bg-surface-variant/40 px-2.5 py-1"
                  >
                    <span className="text-outline">{c.label}</span>
                    <span className="text-neutral-800">{c.value}</span>
                  </span>
                ))}
                {ipSeed?.loglinePinned && (
                  <span className="line-clamp-1 max-w-[40rem] text-neutral-500">
                    ⌗ {ipSeed.loglinePinned}
                  </span>
                )}
              </div>
            )}
          </div>
          {writerCompletedAt && directorInitializedAt ? (
            <Button
              variant="agent"
              agentColor="director"
              size="sm"
              onClick={() =>
                navigate({
                  to: '/director/$episodeId' as never,
                  params: { episodeId } as never,
                })
              }
              className="shrink-0"
              title="进入制作工作台"
            >
              <Clapperboard className="h-4 w-4" />
              进入制作工作台
            </Button>
          ) : writerCompletedAt ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDirectorOpen(true)}
              className="shrink-0"
              title="查看制作台初始化进度"
            >
              <Clapperboard className="h-4 w-4" />
              制作台初始化中
            </Button>
          ) : status === 'initialized' && agentCompletedAt ? (
            <Button
              variant="agent"
              agentColor="writer"
              size="sm"
              onClick={() => setCompleteOpen(true)}
              className="shrink-0"
              title="完成编剧 · 流转到制作工作台"
            >
              <Clapperboard className="h-4 w-4" />
              完成编剧
            </Button>
          ) : null}
        </header>

        <AudienceLockBanner
          audience={audience}
          status={status}
          onOpen={() => setLockOpen(true)}
        />

        <Tabs value={activePath} onValueChange={(v) => setActivePath(v as PathId)} className="mt-4">
          <TabsList>
            {paths.map((p) => {
              const path3Locked = p.id === 'path3' && !agentCompletedAt;
              const disabled = (isDraft && p.id !== 'path1') || path3Locked;
              const title = path3Locked
                ? '分集脚本与 Atlas 全部生成后方可微调'
                : undefined;
              return (
                <TabsTrigger key={p.id} value={p.id} disabled={disabled} title={title}>
                  <p.icon className="h-4 w-4" /> {p.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {paths.map((p) => (
            <TabsContent key={p.id} value={p.id}>
              <Card>
                <CardHeader>
                  <CardTitle>{p.label}</CardTitle>
                  {p.id === 'path3' ? (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-sm text-neutral-500">
                        {hideRefine ? '已按视频时长切分的分集场景 S（本题材无需 AI 润色）' : p.hint}
                      </p>
                      <ExportScriptDropdown
                        episodes={exportEpisodes}
                        ipSeed={ipSeed}
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-neutral-500">{p.hint}</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {p.id === 'path1' && (
                    <HotspotWorkspace
                      ipSeed={ipSeed}
                      onSwitch={setActivePath}
                      loading={query.isLoading}
                      status={status}
                      audienceLocked={Boolean(audience.locked)}
                      onInitialize={() => setConfirmOpen(true)}
                      onReinitialize={() => {
                        if (writerCompletedAt) {
                          toast.error('不能重新项目初始化', '项目已点击完成编剧');
                          return;
                        }
                        const ok = window.confirm(
                          '重新项目初始化会覆盖已生成的分集 S 场景和 Story Atlas 回填内容，保留剧情脚本与受众画像。确定继续？',
                        );
                        if (ok) initializeMut.mutate({ reset: true });
                      }}
                      initializing={initializeMut.isPending}
                      onJumpToAudience={() => {
                        setAtlasTab('audience');
                        setLockOpen(true);
                      }}
                      episodeId={episodeId}
                      episodeCount={episodeCount}
                      episodeDuration={episodeDuration}
                      scriptMode={scriptMode}
                      hasSelectedEpisodeCount={hasSelectedEpisodeCount}
                      worldText={storylineWorld}
                      charactersText={storylineCharacters}
                      episodes={storylineEpisodes}
                      onStorylineSaved={refetchAll}
                      onEpisodeCountSaved={refetchAll}
                      onEpisodeDurationChange={refetchAll}
                      agentCompletedAt={agentCompletedAt}
                      writerCompletedAt={writerCompletedAt}
                      onOpenAgent={() => setAgentOpen(true)}
                    />
                  )}
                  {p.id === 'path3' && (
                    <DialogueWorkspace
                      episodeId={episodeId}
                      onFinish={refetchAll}
                      hideRefine={hideRefine}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {streaming && (
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-brand-50 px-4 py-2 text-sm text-brand-600">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-500 opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-500"></span>
            </span>
            正在生成…SSE 流推中
          </div>
        )}
      </section>

      <StoryAtlasPanel
        episodeId={episodeId}
        status={status}
        onEditAudience={() => setLockOpen(true)}
      />

      <AudienceLockDialog
        open={lockOpen}
        onOpenChange={setLockOpen}
        episodeId={episodeId}
        audience={audience}
        onSaved={refetchAll}
      />

      <InitializeConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        ipSeed={ipSeed}
        audience={audience}
        episodeCount={episodeCount}
        storylineQuick={ipSeed?.storyline ?? ''}
        fullStorylineText={fullStoryline?.text ?? null}
        plotsCount={storylineEpisodes.length}
        initialGenreTags={initialGenreTags}
        onConfirm={(payload) => initializeMut.mutate(payload)}
        submitting={initializeMut.isPending}
      />

      <AgentWorkflowDialog
        open={agentOpen}
        onOpenChange={setAgentOpen}
        episodeId={episodeId}
        onFinish={refetchAll}
        onSwitchToRefine={() => setActivePath('path3')}
        initialEpisodes={initialAgentEpisodes}
        initialTotalEpisodes={episodeCount}
      />

      <DirectorWorkflowDialog
        open={directorOpen}
        onOpenChange={setDirectorOpen}
        episodeId={episodeId}
        onFinish={refetchAll}
        onEnterDirector={() => {
          setDirectorOpen(false);
          navigate({
            to: '/director/$episodeId' as never,
            params: { episodeId } as never,
          });
        }}
      />

      <Modal
        open={completeOpen}
        onOpenChange={(o) => {
          if (!completeMut.isPending) setCompleteOpen(o);
        }}
        title="完成编剧 · 制作初始化配置"
        description="选择本剧的影视风格与画面比例 · 该配置将贯穿人物设定 / 关键帧 / 出片全链路 · 此操作不可撤销"
        className="max-w-md"
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCompleteOpen(false)}
              disabled={completeMut.isPending}
            >
              取消
            </Button>
            <Button
              variant="agent"
              agentColor="writer"
              size="sm"
              onClick={() => completeMut.mutate()}
              disabled={completeMut.isPending || !completeReady}
              title={!completeReady ? '请选择风格 + 画面比例' : undefined}
            >
              {completeMut.isPending ? '处理中…' : '确认完成'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 pb-3">
          <div className="rounded-lg bg-surface-variant/40 p-3 text-xs leading-relaxed text-neutral-700">
            <div>
              剧名:<strong className="font-semibold">「{ipSeed?.ipName || episodeId}」</strong>
            </div>
            {exportEpisodes.length > 0 && (
              <div className="mt-1 text-neutral-500">共 {exportEpisodes.length} 集已生成</div>
            )}
          </div>

          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
              影视风格
            </div>
            <div className="flex flex-wrap gap-1.5">
              {VIDEO_STYLES.map((style) => {
                const active =
                  !completeCustomStyle.trim() && completeVideoStyle === style.name;
                return (
                  <button
                    key={style.name}
                    type="button"
                    title={`${style.tagline}\n适合: ${style.suitableFor.join('、')}`}
                    onClick={() => {
                      setCompleteVideoStyle(style.name);
                      setCompleteCustomStyle('');
                    }}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] transition ${
                      active
                        ? 'border-brand-500 bg-brand-500 text-white'
                        : 'border-outline-variant/40 bg-surface text-neutral-700 hover:border-brand-300'
                    }`}
                  >
                    {style.name}
                  </button>
                );
              })}
            </div>
            {/* 选中风格的说明 */}
            {!completeCustomStyle.trim() && completeVideoStyle && (() => {
              const meta = VIDEO_STYLES.find((s) => s.name === completeVideoStyle);
              if (!meta) return null;
              return (
                <div className="mt-1.5 rounded bg-surface-variant/30 px-2.5 py-1.5 text-[11px] text-neutral-600">
                  <span className="font-medium">{meta.tagline}</span>
                  {' — '}
                  <span className="text-neutral-500">{meta.description}</span>
                  <div className="mt-0.5 text-neutral-400">适合: {meta.suitableFor.join('、')}</div>
                </div>
              );
            })()}
            <input
              type="text"
              value={completeCustomStyle}
              onChange={(e) => setCompleteCustomStyle(e.target.value)}
              maxLength={40}
              placeholder="自定义风格(可选,优先生效)"
              className="mt-2 w-full rounded-md border border-outline-variant/40 bg-surface px-2.5 py-1.5 text-xs outline-none focus:border-brand-500"
            />
          </div>

          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
              画面比例
            </div>
            <div className="inline-flex rounded-md border border-outline-variant/40 bg-surface text-xs">
              {(['9:16', '16:9'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setCompleteAspectRatio(v)}
                  className={`px-3 py-1 transition ${
                    completeAspectRatio === v
                      ? 'bg-brand-500 text-white'
                      : 'text-neutral-600 hover:bg-surface-variant/40'
                  }`}
                >
                  {v === '9:16' ? '竖屏 9:16' : '横屏 16:9'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function AudienceLockBanner({
  audience,
  status,
  onOpen,
}: {
  audience: AudienceState;
  status: 'draft' | 'initialized';
  onOpen: () => void;
}) {
  const locked = audience.locked;
  const projectInitialized = status === 'initialized';
  if (locked) {
    const tags = [
      audience.primary,
      audience.gender && audience.gender !== 'all'
        ? audience.gender === 'female'
          ? '女性向'
          : '男性向'
        : null,
      audience.ageRange ? `${audience.ageRange[0]}-${audience.ageRange[1]} 岁` : null,
      ...(audience.regions ?? []),
      ...(audience.tags ?? []),
    ].filter(Boolean) as string[];
    return (
      <Card className="border-success/30 bg-success-soft/20">
        <CardContent className="flex flex-wrap items-center gap-3 py-3">
          <Lock className="h-5 w-5 shrink-0 text-success" />
          <div className="flex-1 text-sm">
            <strong className="text-success">受众已锁定</strong>
            {projectInitialized && (
              <span className="ml-2 text-xs text-success/80">· 项目已初始化,不可再修改</span>
            )}
            <div className="mt-1 flex flex-wrap gap-1">
              {tags.length > 0 ? (
                tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-success/10 px-2 py-0.5 text-xs text-success"
                  >
                    {t}
                  </span>
                ))
              ) : (
                <span className="text-xs text-neutral-500">未设置受众标签</span>
              )}
            </div>
          </div>
          {!projectInitialized && (
            <Button variant="secondary" size="sm" onClick={onOpen}>
              修改
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-warning/30 bg-warning-soft/30">
      <CardContent className="flex items-center gap-3 py-3">
        <AlertTriangle className="h-5 w-5 shrink-0 text-warning" />
        <div className="flex-1 text-sm">
          <strong>请先锁定目标用户人群画像</strong> · 锁定后,后续 Path / Atlas 内容会按此目标人群差异化
        </div>
        <Button variant="secondary" size="sm" onClick={onOpen}>
          锁定画像
        </Button>
      </CardContent>
    </Card>
  );
}

function EditableIpName({ episodeId, value }: { episodeId: string; value: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const mut = useMutation({
    mutationFn: (ipName: string) =>
      api
        .patch(`writer/episodes/${episodeId}/ip-seed`, { json: { ipName } })
        .json<{ ipSeed: IpSeed; updatedAt: string }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['episode', episodeId] });
      qc.invalidateQueries({ queryKey: ['writer', 'projects'] });
      toast.success('IP 名称已更新');
      setEditing(false);
    },
    onError: (err: Error) => toast.error('保存失败', err.message || '稍后重试'),
  });

  const save = () => {
    const next = draft.trim();
    if (!next || next === value.trim()) {
      setEditing(false);
      return;
    }
    mut.mutate(next);
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-brand-300 bg-surface px-2 py-0.5">
        <span className="text-outline">IP</span>
        <input
          autoFocus
          value={draft}
          maxLength={30}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setEditing(false);
          }}
          disabled={mut.isPending}
          className="w-40 bg-transparent text-neutral-800 outline-none"
        />
        <button type="button" onClick={save} disabled={mut.isPending} className="rounded p-0.5 text-success hover:bg-success-soft/40" title="保存">
          <Check className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => setEditing(false)} disabled={mut.isPending} className="rounded p-0.5 text-neutral-400 hover:bg-surface-variant" title="取消">
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }

  return (
    <span className="group inline-flex items-center gap-1 rounded-full border border-outline-variant/40 bg-surface-variant/40 px-2.5 py-1">
      <span className="text-outline">IP</span>
      <span className="text-neutral-800">{value || '未命名'}</span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="rounded p-0.5 text-neutral-300 transition hover:text-brand-600"
        title="修改 IP 名称"
      >
        <Pencil className="h-3 w-3" />
      </button>
    </span>
  );
}

function inferEpisodeCountFromStoryline(storyline?: string): number {
  if (!storyline) return 0;
  const matches = Array.from(storyline.matchAll(/(?:第\s*(\d+)\s*集|Episode\s*(\d+))/gi));
  if (!matches.length) return 0;
  return Math.max(...matches.map((match) => Number(match[1] || match[2] || 0)));
}

function StatusChip({
  status,
  initializedAt,
}: {
  status: 'draft' | 'initialized';
  initializedAt: string | null;
}) {
  if (status === 'initialized') {
    const t = initializedAt ? new Date(initializedAt) : null;
    const short = t
      ? `${t.getMonth() + 1}/${t.getDate()} ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`
      : '';
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-success-soft/60 px-2.5 py-1 text-success">
        <CheckCircle2 className="h-3.5 w-3.5" />
        <span className="font-semibold">已初始化</span>
        {short && <span className="text-success/80">· {short}</span>}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft/60 px-2.5 py-1 text-warning">
      <CircleDot className="h-3.5 w-3.5" />
      <span className="font-semibold">待初始化</span>
    </span>
  );
}
